import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { isOrderAlreadyCreated } from '../utils/orderCreation';
import { countVariantOccupiedSeats } from '../utils/guestEventEligibility';
import {
  filterFutureObjects,
  getRecurringStartAndEndDate,
  getVariantPrice,
  sortVariantiByDate,
} from '../utils';
import { fetchUserDecks, fetchDecksByIds } from '../utils/deckApi';
import {
  eventAllowsPuntiPayment,
  eventAllowsPublicNonPuntiPayment,
} from '../utils/eventPuntiPayment';
import { CIL_POINTS_LABEL, formatCilPoints } from '../utils/cilPoints';
import { orderOutcome, track } from '../utils/umami';

const italianDays = {
  Domenica: 0,
  Lunedì: 1,
  Martedì: 2,
  Mercoledì: 3,
  Giovedì: 4,
  Venerdì: 5,
  Sabato: 6,
};

/**
 * Hook that encapsulates event booking state and logic (variants, pricing, payment, drawer).
 * Used by EventDetail and BookingDrawerIntegration (Centro Estivo 2026 landing).
 * @param {Object} event - Event object (with Varianti, prezzo, Sconto_Fratelli, Sconto_MVP, etc.)
 * @param {Object} userData - Current user (tesseramenti, orders, stripeCustomerId)
 * @param {Object} options - { isOpen, onOpen, onClose, onOrderSuccess, requestUserData, refetchEvent }
 */
export function useEventBooking(event, userData, options = {}) {
  const {
    isOpen,
    onOpen,
    onClose,
    onOrderSuccess,
    requestUserData,
    refetchEvent,
  } = options;

  const [placement, setPlacement] = useState(
    typeof window !== 'undefined' && window.innerWidth < 600
      ? 'bottom'
      : 'right'
  );
  const [clientSecret, setClientSecret] = useState('');
  const [paymentError, setPaymentError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedVariantPriceOptions, setSelectedVariantPriceOptions] =
    useState({});
  const [clientPaymentIntentId, setClientPaymentIntentId] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [priceOption, setPriceOption] = useState('');
  const [selectedDeck, setSelectedDeck] = useState('');
  const [userDecks, setUserDecks] = useState([]);
  const [participantDecks, setParticipantDecks] = useState([]);
  const [loadingDecks, setLoadingDecks] = useState(false);
  const [variantSelectionsConfirmed, setVariantSelectionsConfirmed] =
    useState(false);
  const [preselectedVariantId, setPreselectedVariantId] = useState(null);
  const prevParticipantRef = useRef(selectedParticipant);

  useEffect(() => {
    const updatePlacement = () => {
      setPlacement(
        typeof window !== 'undefined' && window.innerWidth < 600
          ? 'bottom'
          : 'right'
      );
    };
    window.addEventListener('resize', updatePlacement);
    return () => window.removeEventListener('resize', updatePlacement);
  }, []);

  useEffect(() => {
    const loadUserDecks = async () => {
      if (userData?.id) {
        try {
          const decks = await fetchUserDecks();
          setUserDecks(decks);
        } catch (err) {
          // skip: user decks load failed
        }
      }
    };
    loadUserDecks();
  }, [userData?.id]);

  useEffect(() => {
    const loadParticipantDecks = async () => {
      if (event?.checkoutMode === 'deck' && event?.orders) {
        setLoadingDecks(true);
        try {
          const deckIds = new Set();
          event.orders.forEach((order) => {
            if (order.metadata?.['deck-documentId']) {
              deckIds.add(order.metadata['deck-documentId']);
            }
            if (order.metadata?.products) {
              order.metadata.products.forEach((product) => {
                if (product['deck-documentId']) {
                  deckIds.add(product['deck-documentId']);
                }
              });
            }
          });
          const deckIdsArray = Array.from(deckIds);
          if (deckIdsArray.length > 0) {
            const decks = await fetchDecksByIds(deckIdsArray);
            setParticipantDecks(decks);
          } else {
            setParticipantDecks([]);
          }
        } catch (err) {
          setParticipantDecks([]);
        } finally {
          setLoadingDecks(false);
        }
      }
    };
    loadParticipantDecks();
  }, [event?.checkoutMode, event?.documentId]);

  // Soci unici più partecipanti ospite: stessa regola del server
  const calculateVariantAvailableSeats = (variant) =>
    variant?.Capienza
      ? variant.Capienza - countVariantOccupiedSeats(variant)
      : 999;

  let userOrders = userData?.orders || [];
  if (event?.Recurring) {
    const targetDay = italianDays[event.Recurring];
    if (targetDay !== undefined) {
      const { startDate, endDate } = getRecurringStartAndEndDate(targetDay);
      userOrders = (userData?.orders || []).filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }
  }

  const filteredVarianti = sortVariantiByDate(
    filterFutureObjects(
      event?.Varianti?.filter((variant) => {
        const availableSeats = calculateVariantAvailableSeats(variant);
        if (selectedParticipant) {
          const socioOrders = userData?.orders?.filter(
            (order) =>
              order.socio?.documentId === selectedParticipant &&
              order?.event?.documentId === event?.documentId
          );
          const uniqueProducts = Array.from(
            new Set(
              socioOrders?.reduce((acc, order) => {
                const products =
                  order?.metadata?.products?.map((product) => product.id) || [];
                return [...acc, ...products];
              }, []) || []
            )
          );
          if (
            availableSeats < 1 ||
            (uniqueProducts && uniqueProducts.includes(variant.documentId))
          ) {
            return false;
          }
        }
        return availableSeats > 0;
      })
    )
  );

  const allVarianti = sortVariantiByDate(
    filterFutureObjects(event?.Varianti || [])
  );

  const hasAlreadyPurchasedVariant = (variantId) => {
    if (!selectedParticipant) return false;
    const socioOrders = userData?.orders?.filter(
      (order) =>
        order.socio?.documentId === selectedParticipant &&
        order?.event?.documentId === event?.documentId
    );
    const uniqueProducts = Array.from(
      new Set(
        socioOrders?.reduce((acc, order) => {
          const products =
            order?.metadata?.products?.map((product) => product.id) || [];
          return [...acc, ...products];
        }, []) || []
      )
    );
    return uniqueProducts.includes(variantId);
  };

  const ordersFromOtherParticipants = {};
  userOrders.forEach((o) =>
    o?.metadata?.products?.forEach((p) => {
      if (
        filteredVarianti?.map((v) => v.documentId).includes(p.id) &&
        o.socio.documentId !== selectedParticipant
      ) {
        ordersFromOtherParticipants[p.id] = p.id;
      }
    })
  );

  const getDisabledPartecipant = (tesseramentoId) => {
    const socioOrders = userOrders?.filter(
      (order) =>
        order.socio?.documentId === tesseramentoId &&
        order?.event?.documentId === event?.documentId
    );
    const uniqueProducts = Array.from(
      new Set(
        socioOrders?.reduce((acc, order) => {
          const products =
            order?.metadata?.products?.map((product) => product.id) || [];
          return [...acc, ...products];
        }, []) || []
      )
    );
    if (filteredVarianti?.length) {
      return filteredVarianti.every(
        (variant) =>
          uniqueProducts && uniqueProducts.includes(variant.documentId)
      );
    }
    return socioOrders?.length > 0;
  };

  const handlePriceOptionChange = ({
    variantId,
    name,
    applyDiscount,
    priceOption: priceOptionUpdate,
  }) => {
    setSelectedVariantPriceOptions((prevData) => {
      if (prevData[variantId] === priceOptionUpdate || !priceOptionUpdate) {
        const { [variantId]: removed, ...rest } = prevData;
        return rest;
      }
      return {
        ...prevData,
        [variantId]: {
          type: 'variant',
          name: `${event?.Nome} - ${name}`,
          applyDiscount,
          priceOption: priceOptionUpdate,
        },
      };
    });
  };

  const checkIfDeckSelectionRequired = () => {
    if (!event?.checkoutMode) return false;
    return event.checkoutMode === 'deck';
  };

  const onModalClose = () => {
    setPaymentError('');
    setClientSecret('');
    setClientPaymentIntentId('');
    setPaymentSuccess(false);
    setPaymentLoading(false);
    setSelectedParticipant(null);
    setPaymentMethod('');
    setPriceOption('full');
    setSelectedVariantPriceOptions({});
    setSelectedDeck('');
    setVariantSelectionsConfirmed(false);
    setPreselectedVariantId(null);
    if (typeof onClose === 'function') onClose();
  };

  // Reset drawer state when it opens (e.g. user clicks Iscriviti again)
  useEffect(() => {
    if (isOpen) {
      setShowPaymentForm(false);
      setClientSecret('');
      setClientPaymentIntentId('');
      setPaymentError(null);
      setSelectedDeck('');
      setVariantSelectionsConfirmed(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const prev = prevParticipantRef.current;
    if (
      prev != null &&
      selectedParticipant != null &&
      prev !== selectedParticipant
    ) {
      setVariantSelectionsConfirmed(false);
      setShowPaymentForm(false);
      setClientSecret('');
      setClientPaymentIntentId('');
      setPaymentError(null);
      setPaymentMethod('');
    }
    prevParticipantRef.current = selectedParticipant;
  }, [selectedParticipant]);

  const handleOpenDrawer = (variant) => {
    const picked = variant?.documentId ? variant : null;
    setShowPaymentForm(false);
    setClientSecret('');
    setClientPaymentIntentId('');
    setPaymentError(null);
    setSelectedDeck('');
    setVariantSelectionsConfirmed(false);
    setPreselectedVariantId(picked?.documentId || null);
    if (picked && !event?.accontoAmount) {
      setSelectedVariantPriceOptions({
        [picked.documentId]: {
          type: 'variant',
          name: `${event?.Nome} - ${picked.Nome}`,
          applyDiscount: false,
          priceOption: 'totale',
        },
      });
    } else {
      setSelectedVariantPriceOptions({});
    }
    if (typeof onOpen === 'function') onOpen();
  };

  const handleDeckSelectionChange = (deck) => {
    setSelectedDeck(deck);
  };

  const completeOrderSuccess = () => {
    setPaymentSuccess(true);
    if (typeof requestUserData === 'function')
      requestUserData({ withLoading: false });
    if (typeof refetchEvent === 'function') refetchEvent();
    setTimeout(() => {
      onModalClose();
      if (typeof onOrderSuccess === 'function') onOrderSuccess();
    }, 1000);
  };

  // Statistiche: la variante si indica solo se ne è stata scelta una
  const trackedBooking = () => {
    const variantIds = Object.keys(selectedVariantPriceOptions || {});
    return {
      event_id: event?.documentId,
      variant_id: variantIds.length === 1 ? variantIds[0] : undefined,
      flow: 'socio',
      partecipanti: 1,
    };
  };

  const createOrder = async ({
    paymentIntentId,
    paymentStatus = 'pending',
    paymentMethod: orderPaymentMethod = 'contanti',
    paymentMethodId = '',
    participantId = '',
    deckDocumentId,
    paymentIntents: paymentIntentsOverride,
    amount: amountOverride,
  } = {}) => {
    setPaymentLoading(true);
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !(selectedDeck || deckDocumentId)) {
      setPaymentError('Seleziona un mazzo per continuare');
      setPaymentLoading(false);
      return;
    }
    // Con carta il checkout è già partito alla creazione del pagamento
    if (orderPaymentMethod !== 'elettronico') {
      const { metodo } = orderOutcome({
        paymentMethod: orderPaymentMethod,
        paymentStatus,
      });
      track.checkoutStart({ ...trackedBooking(), metodo });
    }
    try {
      const isVariant =
        selectedVariantPriceOptions &&
        Object.keys(selectedVariantPriceOptions).length > 0;
      let variantAmount = 0;
      const areAllTotal = Object.keys(selectedVariantPriceOptions).every(
        (productId) =>
          selectedVariantPriceOptions[productId].priceOption === 'totale'
      );
      const areAllVariantSelected =
        Object.keys(selectedVariantPriceOptions).length ===
        (event?.Varianti?.length || 0);

      if (isVariant) {
        variantAmount = Object.keys(selectedVariantPriceOptions).reduce(
          (acc, variantId) => {
            const variantOption = selectedVariantPriceOptions[variantId];
            const variant = event?.Varianti?.find(
              (v) => v.documentId === variantId
            );
            const variantPrice = getVariantPrice(variant, event);
            if (variantOption.priceOption === 'acconto')
              return acc + (event?.accontoAmount || 0);
            if (variantOption.applyDiscount) {
              return (
                acc + (variantPrice - (parseFloat(event?.Sconto_Fratelli) || 0))
              );
            }
            return acc + variantPrice;
          },
          0
        );
      }
      if (
        areAllTotal &&
        areAllVariantSelected &&
        event?.Sconto_MVP != null &&
        event?.Sconto_MVP !== ''
      ) {
        variantAmount = Number(event.Sconto_MVP) || 0;
      }

      const response = await axios.post('/api/ordini', {
        eventId: event.documentId,
        participantId: selectedParticipant || participantId,
        paymentIntentId,
        paymentStatus,
        paymentMethod: orderPaymentMethod,
        paymentMethodId,
        amount: amountOverride ?? (isVariant ? variantAmount : event.prezzo),
        paymentIntents: paymentIntentsOverride || clientPaymentIntentId,
        deckDocumentId: selectedDeck || deckDocumentId || null,
        ...(isVariant
          ? {
              eventVariants: selectedVariantPriceOptions,
              amount: variantAmount,
            }
          : {
              eventVariants: {
                [event.documentId]: {
                  type: 'event',
                  priceOption: 'totale',
                  name: event.Nome,
                },
              },
            }),
      });

      if (
        isOrderAlreadyCreated(response.data) ||
        response.data?.order ||
        response.status === 200
      ) {
        if (!isOrderAlreadyCreated(response.data)) {
          track.bookingComplete({
            ...trackedBooking(),
            ...orderOutcome({
              paymentMethod: orderPaymentMethod,
              paymentStatus,
              amount: isVariant
                ? variantAmount
                : (amountOverride ?? event.prezzo),
            }),
          });
        }
        completeOrderSuccess();
      }
    } catch (e) {
      if (isOrderAlreadyCreated(e.response?.data)) {
        completeOrderSuccess();
        return;
      }
      // Rifiuto esplicito (es. posti esauriti) senza carta: nessun pagamento è stato incassato
      const status = e.response?.status;
      if (
        orderPaymentMethod !== 'elettronico' &&
        [400, 503].includes(status) &&
        e.response?.data?.error
      ) {
        setPaymentError(e.response.data.error);
        return;
      }
      setPaymentError(
        "Il pagamento è avvenuto con successo ma qualcosa è andato storto con la creazione dell'ordine. Contatta cortesemente il team di supporto."
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const computeBookingAmount = () => {
    const isVariant =
      selectedVariantPriceOptions &&
      Object.keys(selectedVariantPriceOptions).length > 0;
    let variantAmount = 0;
    const areAllTotal = Object.keys(selectedVariantPriceOptions || {}).every(
      (productId) =>
        selectedVariantPriceOptions[productId].priceOption === 'totale'
    );
    const areAllVariantSelected =
      Object.keys(selectedVariantPriceOptions || {}).length ===
      (event?.Varianti?.length || 0);

    if (isVariant) {
      variantAmount = Object.keys(selectedVariantPriceOptions).reduce(
        (acc, variantId) => {
          const variantOption = selectedVariantPriceOptions[variantId];
          const variant = event?.Varianti?.find(
            (v) => v.documentId === variantId
          );
          const variantPrice = getVariantPrice(variant, event);
          if (variantOption.priceOption === 'acconto')
            return acc + (event?.accontoAmount || 0);
          if (variantOption.applyDiscount) {
            return (
              acc + (variantPrice - (parseFloat(event?.Sconto_Fratelli) || 0))
            );
          }
          return acc + variantPrice;
        },
        0
      );
    }
    if (
      areAllTotal &&
      areAllVariantSelected &&
      event?.Sconto_MVP != null &&
      event?.Sconto_MVP !== ''
    ) {
      variantAmount = Number(event.Sconto_MVP) || 0;
    }
    const totalAmount = isVariant ? variantAmount : (event?.prezzo ?? 0);
    return { totalAmount, isVariant, variantAmount };
  };

  const buildPaymentIntentBody = (totalAmount, isVariant, variantAmount) => ({
    currency: 'eur',
    eventUrlTitle: event.urlTitle,
    eventId: event.documentId,
    socioId: selectedParticipant,
    stripeCustomerId: userData?.stripeCustomerId,
    amount: totalAmount * 100,
    selectedDeck: selectedDeck || null,
    ...(isVariant
      ? {
          eventVariants: selectedVariantPriceOptions,
          amount: variantAmount * 100,
        }
      : {
          eventVariants: {
            [event.documentId]: {
              type: 'event',
              priceOption: 'totale',
              amount: event.prezzo,
              name: event.Nome,
            },
          },
        }),
  });

  const payWithPunti = async () => {
    if (!eventAllowsPuntiPayment(event)) {
      setPaymentError(
        `Il pagamento con ${CIL_POINTS_LABEL} non è disponibile per questo evento.`
      );
      return;
    }
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !selectedDeck) {
      setPaymentError('Seleziona un mazzo per continuare');
      return;
    }
    try {
      setPaymentLoading(true);
      const { totalAmount, isVariant, variantAmount } = computeBookingAmount();
      if (totalAmount === 0) {
        await createOrder({
          paymentStatus: 'succeeded',
          deckDocumentId: selectedDeck || null,
        });
        return;
      }
      const userPunti = Number(userData?.punti) || 0;
      if (userPunti < totalAmount) {
        setPaymentError(
          `${CIL_POINTS_LABEL} insufficienti. Hai ${formatCilPoints(userPunti)}, servono ${formatCilPoints(totalAmount)}.`
        );
        return;
      }
      const piResponse = await axios.post('/api/create-payment-intent', {
        ...buildPaymentIntentBody(totalAmount, isVariant, variantAmount),
        paymentMethod: 'punti',
      });
      const localPiId = piResponse.data.localPaymentIntentId;
      await createOrder({
        paymentMethod: 'punti',
        paymentStatus: 'succeeded',
        paymentIntentId: localPiId,
        paymentIntents: localPiId,
        amount: piResponse.data.amount ?? totalAmount,
        deckDocumentId: selectedDeck || null,
      });
    } catch (err) {
      setPaymentError(
        err?.response?.data?.error ||
          err?.message ||
          `Errore nel pagamento con ${CIL_POINTS_LABEL}`
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const createPaymentIntent = async () => {
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !selectedDeck) {
      setPaymentError('Seleziona un mazzo per continuare');
      return;
    }
    if (!eventAllowsPublicNonPuntiPayment(event)) {
      setPaymentError(
        `Per questo evento è disponibile solo il pagamento con ${CIL_POINTS_LABEL}.`
      );
      return;
    }
    try {
      setPaymentLoading(true);
      const { totalAmount, isVariant, variantAmount } = computeBookingAmount();
      if (totalAmount === 0) {
        await createOrder({
          paymentStatus: 'succeeded',
          deckDocumentId: selectedDeck || null,
        });
        setPaymentLoading(false);
        return;
      }
      track.checkoutStart({ ...trackedBooking(), metodo: 'stripe' });
      const response = await axios.post('/api/create-payment-intent', {
        ...buildPaymentIntentBody(totalAmount, isVariant, variantAmount),
      });
      setClientSecret(response.data.clientSecret);
      setClientPaymentIntentId(response.data.localPaymentIntentId);
      setPaymentError(null);
    } catch (err) {
      setPaymentError(
        err?.response?.data?.error || err?.message || 'Errore pagamento'
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const areVariantsSelected = Boolean(
    Object.keys(selectedVariantPriceOptions || {}).length
  );

  const clearPaymentSession = () => {
    setShowPaymentForm(false);
    setClientSecret('');
    setClientPaymentIntentId('');
    setPaymentError(null);
  };

  const confirmVariantSelections = () => {
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !selectedDeck) {
      setPaymentError('Seleziona un mazzo per continuare');
      return;
    }
    if (!Object.keys(selectedVariantPriceOptions || {}).length) return;
    setVariantSelectionsConfirmed(true);
    setPaymentError(null);
  };

  const reopenVariantSelection = () => {
    setVariantSelectionsConfirmed(false);
    clearPaymentSession();
    setPaymentMethod('');
  };

  const handleProceedToPayment = () => {
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !selectedDeck) {
      setPaymentError('Seleziona un mazzo per continuare');
      return;
    }
    createPaymentIntent();
    setShowPaymentForm(true);
  };

  const handleConfirm = async ({ isFree } = {}) => {
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !selectedDeck) {
      setPaymentError('Seleziona un mazzo per continuare');
      return;
    }
    if (paymentMethod === 'punti') {
      if (!eventAllowsPuntiPayment(event)) {
        setPaymentError(
          `Il pagamento con ${CIL_POINTS_LABEL} non è disponibile per questo evento.`
        );
        return;
      }
      await payWithPunti();
      return;
    }
    if (!eventAllowsPublicNonPuntiPayment(event)) {
      setPaymentError(
        `Per questo evento è disponibile solo il pagamento con ${CIL_POINTS_LABEL}.`
      );
      return;
    }
    if (paymentMethod === 'contanti' || isFree) {
      await createOrder({
        paymentStatus: isFree ? 'succeeded' : 'pending',
        deckDocumentId: selectedDeck || null,
      });
      return;
    }
    if (areVariantsSelected) {
      const totalVariantAmount = Object.keys(
        selectedVariantPriceOptions
      ).reduce((acc, variantId) => {
        const variantOption = selectedVariantPriceOptions[variantId];
        const variant = event?.Varianti?.find(
          (v) => v.documentId === variantId
        );
        const variantPrice = getVariantPrice(variant, event);
        if (variantOption.priceOption === 'acconto')
          return acc + (event?.accontoAmount || 0);
        if (variantOption.applyDiscount) {
          return (
            acc + (variantPrice - (parseFloat(event?.Sconto_Fratelli) || 0))
          );
        }
        return acc + variantPrice;
      }, 0);
      if (totalVariantAmount === 0) {
        await createOrder({
          paymentStatus: 'succeeded',
          paymentMethod: 'contanti',
          deckDocumentId: selectedDeck || null,
        });
        return;
      }
    }
    if (!areVariantsSelected) {
      if (event?.prezzo === 0) {
        await createOrder({
          paymentStatus: 'succeeded',
          paymentMethod: 'contanti',
          deckDocumentId: selectedDeck || null,
        });
        return;
      }
      createPaymentIntent();
      setShowPaymentForm(true);
      return;
    }
    createPaymentIntent();
    setShowPaymentForm(true);
  };

  const onPaymentSuccess = async ({
    status,
    paymentIntentId,
    paymentMethodId,
  }) => {
    const requiresDeck = checkIfDeckSelectionRequired();
    if (requiresDeck && !selectedDeck) {
      setPaymentError('Seleziona un mazzo per continuare');
      return;
    }
    await createOrder({
      paymentIntentId,
      paymentMethodId,
      paymentStatus: status,
      paymentMethod: 'elettronico',
      deckDocumentId: selectedDeck || null,
    });
  };

  return {
    placement,
    clientSecret,
    paymentError,
    setPaymentError,
    paymentLoading,
    paymentSuccess,
    selectedParticipant,
    setSelectedParticipant,
    paymentMethod,
    setPaymentMethod,
    showPaymentForm,
    priceOption,
    setPriceOption,
    selectedDeck,
    userDecks,
    participantDecks,
    loadingDecks,
    selectedVariantPriceOptions,
    handlePriceOptionChange,
    filteredVarianti: filteredVarianti || [],
    allVarianti: allVarianti || [],
    ordersFromOtherParticipants,
    getDisabledPartecipant,
    hasAlreadyPurchasedVariant,
    handleOpenDrawer,
    onModalClose,
    createOrder,
    createPaymentIntent,
    payWithPunti,
    computeBookingAmount,
    handleConfirm,
    handleProceedToPayment,
    onPaymentSuccess,
    handleDeckSelectionChange,
    variantSelectionsConfirmed,
    confirmVariantSelections,
    reopenVariantSelection,
    preselectedVariantId,
  };
}
